import React, { useState, useEffect } from 'react';
import { Settings, User, ChevronLeft, ChevronRight, Drill, Gem, Activity, Banknote, Train } from 'lucide-react';
import './FundsPage.css';
import {formatCurrency, formatInterest} from "../../util/currency_lookup";
import {generateDateRanges} from "../../util/date_generate";
import {Card, CardContent, StatCard} from "../../components/card-components";
import DashboardBase  from "../DashboardBase";
import { Loader } from '../../components/loader';
import { useNavigate } from "react-router";
import CurrencySearch from '../../components/CurrencySearch';
import { useAuth } from '../../context/AuthContext';
import CurrencySelector from '../../components/CurrencySelector';
import {ROLES} from "../../config/routes";

const FundsPage = () => {
  const [funds, setFunds] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState({
    table: true,
    stats: true,
  });
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currency, setSelectedCurrency] = useState(null);
  const [summarySelectedCurrency, setSummarySelectedCurrency] = useState('GBP');
  const [timePeriod, setTimePeriod] = useState("lastSixMonths");
  const { isLoggedIn, userRole, login, logout } = useAuth();
  
  const navigate = useNavigate();

  // Keep your existing transactions array
  const transactions = [/* ... your existing transactions data ... */];

  useEffect(() => {
    const timer = setTimeout(() => {
        setDebouncedSearch(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect((loading) => {
    if (!loading) {
      fetchTransactions(currentPage, searchQuery, currency);
      fetchStats(summarySelectedCurrency, timePeriod);
    }
  }, [currentPage, debouncedSearch]);

  useEffect((loading) => {
      fetchTransactions(currentPage, searchQuery, currency);
      fetchStats(summarySelectedCurrency, timePeriod);
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when search changes
    fetchTransactions(1, debouncedSearch, currency);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchStats(summarySelectedCurrency, timePeriod);;
  }, [timePeriod]);

  const fetchTransactions = (page, search = '', currency) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate(`/login`);
    }
    const indexOfLastTransaction = ((page - 1) * itemsPerPage);
    let url = `${process.env.REACT_APP_API_URL}/api/v1/funds?offset=${indexOfLastTransaction}&limit=${itemsPerPage}&search=${search}`;
    if (currency) {
      url += `&currency_code=${encodeURIComponent(currency.currency_code)}`;
    }

    fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        console.log(data);
        setFunds(data.rows);
        setTotal(data.total);
        setLoading(prev => ({ ...prev, table: false }));
      })
      .catch((error) => {
        setError(error);
        setLoading(prev => ({ ...prev, table: false }));
      });
  };

  const fetchStats = (selectCurrency, timePeriod) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate(`/login`);
    }

    let url = `${process.env.REACT_APP_API_URL}/api/v1/funds/investment-top/${selectCurrency}/overview`;
    if (timePeriod) {
      let filter = generateDateRanges();
      let dates = filter[timePeriod]
      url += `?&start_date=${encodeURIComponent(dates.start)}&end_date=${encodeURIComponent(dates.end)}`;
    }
    fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        console.log(data);
        setStats(data);
        setLoading(prev => ({ ...prev, stats: false }));
      })
      .catch((error) => {
        setError(error);
        setLoading(prev => ({ ...prev, stats: false }));
      });
  };

  const goToItem = (id) => {
    navigate(`/fund-details/${id}`);
  };

  const goToItemInvest = (id) => {
    navigate(`/fund-investment-details/${id}`);
  };

  const goToUserDrillDown = (id) => {
    navigate(`/fund-user-drilldown/${id}`);
  };

  const handleCurrencySelect = (icurrency) => {
    // Call your fetchTransactions with the selected fund
    setSelectedCurrency(icurrency);
    setCurrentPage(1);
    fetchTransactions(1, searchQuery, icurrency);
  };

  const handleCurrencyChange = (currency) => {
    setSummarySelectedCurrency(currency);
    fetchStats(currency, timePeriod);
};

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getStatusClass = (status) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'status-completed';
      case 'pending': return 'status-pending';
      case 'rejected': return 'status-rejected';
      default: return 'status-default';
    }
  };

  const goToCreate = () => {
    navigate(`/create-fund`);
  };

  const isLoading = Object.values(loading).some(status => status);
  if (isLoading) {
    return <Loader loading={true} />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <DashboardBase
      page="funds"
      content= {
            <div className="dashboard-home">
                {/* Secondary Navigation */}

                <div className="dashboard-header">
                        <h1>Summary</h1>

                        
                        <div className="sidenote">
                            {userRole == ROLES.ADMIN ? 
                              <button
                                      onClick={() => goToCreate()}
                                      type="button" 
                                      className="button secondary">
                              Create Fund</button> : <></>}
                              <CurrencySelector 
                                selectedCurrency={summarySelectedCurrency}
                                onCurrencyChange={handleCurrencyChange}/>
                              <select
                                value={timePeriod} 
                                onChange={(e) => setTimePeriod(e.target.value)}
                                className="select-control">
                                    <option value={"thisWeek"} >This week</option>
                                    <option value={"thisMonth"} >This month</option>
                                    <option value={"lastSixMonths"} >Last six months</option>
                                    <option value={"lastYear"} >Last year</option>
                              </select>
                        </div>
                </div>

                {/* Stats Grid */}
                <div className="stats-grid">
                    <div className="stats-container">
                        <StatCard 
                          title="Net Investments" 
                          value={formatCurrency(stats.net_investment.amount, stats.net_investment.currency)} 
                          icon={() => <div className="text-xs"><Gem/></div>}
                        />
                        <StatCard 
                          title="Investment" 
                          value={formatCurrency(stats.investments.total, stats.investments.currency)} 
                          icon={() => <div className="text-xs"><Activity/></div>}
                        />
                        <StatCard 
                            title="Redemption" 
                            value={formatCurrency(stats.redemptions.total, stats.redemptions.currency)} 
                            icon={() => <div className="text-xs"><Banknote/></div>}
                        />
                        <StatCard 
                          title="Average investment" 
                          value={formatCurrency(stats.investments.daily_average, stats.redemptions.currency)} 
                          icon={() => <div className="text-xs"><Train/></div>}
                        />
                    </div>
                </div>

                {/* Transactions Table */}
                <div className="transactions-section">
                <div className="stats-header">
                    <h2 className="stats-title">Money Market Funds</h2>
                    <select className="select-control">
                    <option>All</option>
                    </select>
                </div>
                <Card>
                    <div className="tabs-header">
                        <div className="tabs">
                            <div className="filters-container">
                                <div className="search-section">
                                    <CurrencySearch onCurrencySelect={handleCurrencySelect} />
                                </div>
                                <div className="search-section">
                                    <input
                                        type="text"
                                        placeholder="Search money market funds..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="search-input"
                                    />
                                    {isLoading && <span className="search-loading">Searching...</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="table-container">
                    <table>
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>Fund Manager</th>
                            <th>Symbol</th>
                            <th>Currency</th>
                            <th>Min investment</th>
                            <th>Last Year Returns</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {funds.map((transaction, idx) => (
                            <tr key={idx} className="table-row" >
                            <td onClick={() => goToItem(transaction.fund_id)}> 
                              <span className="highlight-underline">
                                  {transaction.name}
                              </span>
                            </td>
                            <td>{transaction.fund_manager_name}</td>
                            <td onClick={() => goToItemInvest(transaction.fund_id)}>
                              <span className="my-span">
                              {transaction.symbol}
                              </span>
                            </td>
                            <td>
                                <div className="currency-badge-colored ">
                                {transaction.currency_code}
                                </div>
                            </td>
                            <td>{formatCurrency(transaction.min_addition_value, transaction.currency_code)}</td>
                            <td>
                                {formatInterest(transaction.interest_rate)}
                            </td>
                            <td> 
                                <Drill
                                  onClick={() => goToUserDrillDown(transaction.fund_id)} 
                                  className="icon" size={20}/> 
                            </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    </div>
                    {/* Pagination */}
                    <div className="pagination">
                    <div>
                        <p className="pagination-text">
                        Showing page {currentPage} of {Math.ceil(total / itemsPerPage)}
                        </p>
                    </div>
                    <div className="pagination-buttons">
                        <button
                        onClick={() => paginate(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="pagination-button"
                        >
                        <ChevronLeft className="icon" />
                        </button>
                        <button
                        onClick={() => paginate(p => p + 1)}
                        disabled={currentPage * itemsPerPage >= total}
                        className="pagination-button"
                        >
                        <ChevronRight className="icon" />
                        </button>
                    </div>
                    </div>
                </Card>
            </div>
        </div>
      }
    />
  );
};

export default FundsPage;
